import { effect, inject, Injectable, signal } from '@angular/core';

import { SearchRequestModel } from '~app/models/requests/search-request.model';
import { Utils } from '~core/utils/tools/utils.tools';
import { DictionaryType } from '~shared/core/types/dictionary.type';
import { SettingsStorage } from './storages/settings.storage';

@Injectable({
    providedIn: 'root'
})
export class SavedFiltersService {
    /**
     * saved filters dictionary. key is store base url
     */
    readonly currentSavedFilters = signal<DictionaryType<Pick<SearchRequestModel, 'id' | 'name' | 'sortings' | 'filters'>[]>>({});

    private readonly settingsStorage = inject(SettingsStorage);

    constructor() {
        this.currentSavedFilters.set(this.settingsStorage.getSavedFilters() || {});

        effect(() => this.settingsStorage.saveSavedFilters(this.currentSavedFilters()));
    }

    add(path: string, searchRequestModel: Pick<SearchRequestModel, 'name' | 'sortings' | 'filters'>): string {
        const allSavedFilters = this.currentSavedFilters();
        const id = Utils.newGuid();

        if (!allSavedFilters[path]) {
            allSavedFilters[path] = [];
        }

        this.currentSavedFilters.set({
            ...allSavedFilters,
            [path]: [
                ...allSavedFilters[path],
                {
                    id,
                    name: searchRequestModel.name,
                    sortings: searchRequestModel.sortings,
                    filters: searchRequestModel.filters
                }
            ]
        });

        return id;
    }

    update(path: string, id: string, searchRequestModel: Pick<SearchRequestModel, 'name' | 'sortings' | 'filters'>): void {
        const allSavedFilters = this.currentSavedFilters();

        if (!allSavedFilters[path] || !allSavedFilters[path].some(filter => filter.id === id)) {
            return;
        }

        this.currentSavedFilters.set({
            ...allSavedFilters,
            [path]: [
                ...allSavedFilters[path].filter(filter => filter.id !== id),
                {
                    id,
                    name: searchRequestModel.name,
                    sortings: searchRequestModel.sortings,
                    filters: searchRequestModel.filters
                }
            ]
        });
    }

    remove(path: string, id: string): void {
        const allSavedFilters = this.currentSavedFilters();

        if (!allSavedFilters[path]) {
            return;
        }

        this.currentSavedFilters.set({
            ...allSavedFilters,
            [path]: allSavedFilters[path].filter(filter => filter.id !== id)
        });
    }
}
